import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Calendar, Check, CreditCard, Mail } from "lucide-react";
import { toast } from "sonner";
import BillingAddOnCart from "@/components/BillingAddOnCart";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { isPlatformSession } from "@/lib/platform-api";
import { DEFAULT_USD_TO_SGD, formatMoneyDisplay, sgdToUsd } from "@/lib/money-display";
import { API_BASE } from "@/lib/utils";

const DISPLAY_CURRENCY_KEY = "aivory.billingDisplayCurrency";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: "long" });
  } catch {
    return iso;
  }
}

function invoiceAmount(inv, displayCurrency, rate) {
  const currency = String(inv?.currency || "SGD").toUpperCase();
  const raw = Number(inv?.amountUsd ?? inv?.amount ?? 0);
  if (currency === "SGD") {
    return formatMoneyDisplay(raw, displayCurrency, rate);
  }
  if (displayCurrency === "USD") {
    return formatMoneyDisplay(raw * rate, "USD", rate);
  }
  return formatMoneyDisplay(raw * rate, "SGD", rate);
}

function planRank(id) {
  const order = ["starter", "studio", "scale", "enterprise"];
  return order.indexOf(id);
}

function UsageMeter({ label, used = 0, limit, percent = 0, hint }) {
  const cap = limit == null ? null : Number(limit);
  const pct = Math.min(100, Math.round(Number(percent) || 0));
  const nearCap = cap != null && cap > 0 && pct >= 85;
  return (
    <div>
      <div className="flex justify-between gap-2 text-xs">
        <span className="text-[var(--text-secondary)]">{label}</span>
        <span className="font-mono text-[var(--text-primary)]">
          {used.toLocaleString()}
          {cap != null && cap > 0 && cap < 999 ? ` / ${cap.toLocaleString()}` : cap == null ? "" : " · unlimited"}
        </span>
      </div>
      <div className="mt-2 h-1.5 bg-[var(--bg-elevated)] overflow-hidden">
        <div
          className={`h-full transition-all ${nearCap ? "bg-[var(--primary)]" : "bg-[var(--text-tertiary)]"}`}
          style={{ width: `${cap != null && cap > 0 && cap < 999 ? pct : used > 0 ? 8 : 0}%` }}
        />
      </div>
      {hint ? <p className="mt-1 text-[10px] text-[var(--text-tertiary)]">{hint}</p> : null}
    </div>
  );
}

export default function Billing() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [settings, setSettings] = useState({ billingEmail: "", monthlyLimitUsd: 0 });
  const [savingSettings, setSavingSettings] = useState(false);
  const [paymentReturn, setPaymentReturn] = useState("");
  const [billingCycle, setBillingCycle] = useState("annual");
  const [subscriptionBusy, setSubscriptionBusy] = useState(false);
  const [displayCurrency, setDisplayCurrency] = useState(() => {
    try {
      return localStorage.getItem(DISPLAY_CURRENCY_KEY) || "SGD";
    } catch {
      return "SGD";
    }
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentHandledRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [ovRes, invRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/billing/overview`, { headers }),
        fetch(`${API_BASE}/api/admin/billing/invoices`, { headers }),
      ]);
      if (!ovRes.ok) throw new Error(`Billing overview failed (${ovRes.status})`);
      const ov = await ovRes.json();
      setOverview(ov);
      setSettings(ov.settings || {});
      const cycle = ov.subscription?.billingCycle || ov.billing?.billingCycle || "annual";
      setBillingCycle(cycle === "monthly" ? "monthly" : "annual");
      if (invRes.ok) {
        const inv = await invRes.json();
        setInvoices(inv.invoices || []);
      }
    } catch (err) {
      toast.error("Could not load billing", { description: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const payment = searchParams.get("payment");
    if (!payment || paymentHandledRef.current) return;
    paymentHandledRef.current = true;
    setPaymentReturn(payment);
    setSearchParams({}, { replace: true });

    if (payment === "success") {
      toast.success("Payment successful");
      load();
      const retry1 = window.setTimeout(load, 2500);
      const retry2 = window.setTimeout(load, 6000);
      return () => {
        window.clearTimeout(retry1);
        window.clearTimeout(retry2);
      };
    }
    if (payment === "cancelled" || payment === "failed") {
      toast.message("Payment not completed", {
        description: "You can try again from the invoice or plan section below.",
      });
    }
    return undefined;
  }, [searchParams, setSearchParams, load]);

  const sub = overview?.subscription || {};
  const plans = overview?.plans || [];
  const usage = overview?.usage || {};
  const currentPlan = String(sub.plan || "studio").toLowerCase();
  const currentRank = planRank(currentPlan);
  const onTrial = Boolean(sub.onTrial) || String(sub.orgStatus || "").toLowerCase() === "trial";
  const trialEndsAt = sub.trialEndsAt || sub.revolutTrialEndDate;
  const usdToSgdRate = Number(overview?.billing?.usdToSgdRate || sub.pricing?.usdToSgdRate || DEFAULT_USD_TO_SGD);
  const pricing = sub.pricing || {};
  const monthlySgd = Number(pricing.monthlySgd) || 0;
  const annualSgd = Number(pricing.annualSgd) || 0;

  const setCurrency = (currency) => {
    setDisplayCurrency(currency);
    try {
      localStorage.setItem(DISPLAY_CURRENCY_KEY, currency);
    } catch {
      /* private mode */
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/billing/settings`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(settings),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Save failed");
      toast.success("Billing settings saved");
      setSettings(data.settings || settings);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const revolutEnabled = overview?.paymentsMode === "revolut_subscription";

  const subscribeToPlan = async (planId) => {
    if (!revolutEnabled) {
      toast.message("Self-serve checkout unavailable", {
        description: "Contact ops@aivoryems.com or use Contact sales for Enterprise.",
      });
      return;
    }
    setSubscriptionBusy(true);
    try {
      const redirectUrl = `${window.location.origin}/admin/billing?payment=success`;
      const res = await fetch(`${API_BASE}/api/admin/billing/checkout`, {
        method: "POST",
        headers: { "content-type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ plan: planId, billingCycle: "annual", redirectUrl }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Checkout failed");
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      await startSubscriptionSetup(planId);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubscriptionBusy(false);
    }
  };

  const startSubscriptionSetup = async (planId) => {
    setSubscriptionBusy(true);
    try {
      const redirectUrl = `${window.location.origin}/admin/billing?payment=success`;
      const targetPlan = planId || currentPlan;
      const res = await fetch(`${API_BASE}/api/admin/billing/subscription/setup`, {
        method: "POST",
        headers: { "content-type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ redirectUrl, plan: targetPlan, billingCycle: "annual" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Could not start billing setup");
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      toast.success("Subscription linked", { description: "Your payment method is on file." });
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubscriptionBusy(false);
    }
  };

  const saveBillingCycle = async (cycle) => {
    setBillingCycle(cycle);
    if (!revolutEnabled) return;
    setSubscriptionBusy(true);
    try {
      const redirectUrl = `${window.location.origin}/admin/billing?payment=success`;
      const res = await fetch(`${API_BASE}/api/admin/billing/subscription/cycle`, {
        method: "PATCH",
        headers: { "content-type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ billingCycle: cycle, redirectUrl }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Could not update billing cycle");
      if (data.checkoutUrl) {
        toast.message("Confirm payment method", {
          description: "Complete Revolut checkout to apply your new billing cycle.",
        });
        window.location.href = data.checkoutUrl;
        return;
      }
      toast.success("Billing cycle updated");
      await load();
    } catch (err) {
      toast.error(err.message);
      await load();
    } finally {
      setSubscriptionBusy(false);
    }
  };

  const payInvoice = async (invoiceId) => {
    if (!revolutEnabled) {
      toast.message("Request-only billing", {
        description: "Contact ops@aivoryems.com or submit a recharge request below.",
      });
      return;
    }
    setSubscriptionBusy(true);
    try {
      const redirectUrl = `${window.location.origin}/admin/billing?payment=success`;
      const res = await fetch(`${API_BASE}/api/admin/billing/checkout`, {
        method: "POST",
        headers: { "content-type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ invoiceId, redirectUrl }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Checkout failed");
      if (data.checkoutUrl) window.location.href = data.checkoutUrl;
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubscriptionBusy(false);
    }
  };

  const downloadInvoice = async (invoiceId) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/billing/invoices/${encodeURIComponent(invoiceId)}/pdf`,
        { headers: { ...getAuthHeaders() } },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      const filename = match?.[1] || `invoice-${invoiceId}.html`;
      const url = URL.createObjectURL(blob);
      const opened = window.open(url, "_blank");
      if (!opened) {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err.message || "Could not download invoice");
    }
  };

  const access = overview?.access || {};

  if (loading && !overview) {
    return (
      <div
        className="p-8 lg:p-12 max-w-[1200px] mx-auto min-h-[50vh] text-[var(--text-secondary)] bg-[var(--bg-base)]"
        data-testid="billing-page"
      >
        {paymentReturn === "success" ? (
          <div className="surface p-6 border-l-2 border-l-[var(--primary)] mb-6">
            <div className="font-heading text-xl text-[var(--text-primary)]">Payment received</div>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">Loading your billing details…</p>
          </div>
        ) : null}
        Loading billing…
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 max-w-[1200px] mx-auto bg-[var(--bg-base)] min-h-[50vh]" data-testid="billing-page">
      <div className="text-overline">Workspace · billing</div>
      <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium text-[var(--text-primary)]">Billing</h1>
      <p className="mt-3 text-sm text-[var(--text-secondary)] max-w-2xl leading-relaxed">
        Manage your Revolut subscription (monthly or annual with 17% annual savings). During trial, add a payment
        method so billing continues automatically when the trial ends.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
        <span className="text-[var(--text-tertiary)]">Display prices in</span>
        <button
          type="button"
          className={`btn-secondary text-xs py-1 px-3 ${displayCurrency === "SGD" ? "ring-1 ring-[var(--primary)]" : ""}`}
          onClick={() => setCurrency("SGD")}
        >
          SGD$
        </button>
        <button
          type="button"
          className={`btn-secondary text-xs py-1 px-3 ${displayCurrency === "USD" ? "ring-1 ring-[var(--primary)]" : ""}`}
          onClick={() => setCurrency("USD")}
        >
          US$
        </button>
        {displayCurrency === "USD" ? (
          <span className="text-[var(--text-tertiary)] font-mono">1 USD ≈ {usdToSgdRate.toFixed(2)} SGD</span>
        ) : null}
      </div>

      {onTrial && trialEndsAt ? (
        <div className="mt-6 surface p-5 border-l-2 border-l-[var(--primary)]">
          <div className="font-medium text-[var(--text-primary)]">Studio trial</div>
          <p className="mt-2 text-sm text-[var(--text-secondary)] leading-relaxed">
            Your trial ends on{" "}
            <span className="font-mono text-[var(--text-primary)]">{formatDate(trialEndsAt)}</span>.
            {revolutEnabled
              ? " Add a payment method so your subscription continues automatically when the trial ends."
              : " Contact ops@aivoryems.com to enable Revolut billing before the trial ends."}
          </p>
        </div>
      ) : null}

      {revolutEnabled && !sub.grandfathered ? (
        <div className="mt-6 surface p-6 border border-[var(--border-default)]">
          <div className="flex items-start gap-3">
            <CreditCard size={20} className="text-[var(--primary)] shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-[var(--text-primary)]">Payment method (Revolut)</div>
              <p className="mt-1 text-sm text-[var(--text-secondary)] leading-relaxed">
                {sub.paymentMethodSaved
                  ? onTrial && trialEndsAt
                    ? `Payment method saved. Your trial runs until ${formatDate(trialEndsAt)} — billing starts automatically after that.`
                    : "A card is on file for subscription renewals and add-on purchases."
                  : "Secure checkout hosted by Revolut. Required before your trial ends if you want uninterrupted access."}
              </p>
              {!sub.paymentMethodSaved ? (
                <button
                  type="button"
                  className="btn-primary mt-4 text-sm"
                  disabled={subscriptionBusy}
                  onClick={() => startSubscriptionSetup(currentPlan)}
                >
                  {subscriptionBusy ? "Opening Revolut…" : "Add payment method"}
                </button>
              ) : (
                <p className="mt-3 text-xs font-mono text-[var(--primary)]">Payment method saved</p>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {overview?.paymentsMode === "platform_ops" || (isPlatformSession() && overview?.paymentsMode !== "revolut_subscription") ? (
        <div className="mt-6 surface p-4 border-l-2 border-l-[var(--primary)] text-sm text-[var(--text-secondary)]">
          <strong className="text-[var(--text-primary)]">Platform operator view.</strong> Workspace checkout is managed
          per customer account. Configure Revolut keys and sync plans under{" "}
          <Link to="/platform/billing/settings" className="text-[var(--primary)] hover:underline">
            Platform → Revolut
          </Link>
          .
        </div>
      ) : overview?.paymentsMode === "request_only" ? (
        <div className="mt-6 surface p-4 border-l-2 border-l-[var(--primary)] text-sm text-[var(--text-secondary)]">
          <strong className="text-[var(--text-primary)]">Checkout unavailable.</strong> Revolut is not configured on
          this environment. Plan changes and add-ons require <span className="font-mono">ops@aivoryems.com</span> or{" "}
          <Link to="/contact?intent=sales" className="text-[var(--primary)] hover:underline">
            contact sales
          </Link>
          .
        </div>
      ) : null}

      {paymentReturn === "success" ? (
        <div className="mt-6 surface p-5 border-l-2 border-l-[var(--primary)]">
          <div className="font-heading text-lg text-[var(--text-primary)]">Payment successful</div>
          {onTrial && trialEndsAt ? (
            <p className="mt-2 text-sm text-[var(--text-secondary)] leading-relaxed">
              Your payment method is saved. Your <strong className="text-[var(--text-primary)]">Studio trial</strong> continues
              until <span className="font-mono text-[var(--text-primary)]">{formatDate(trialEndsAt)}</span>. Revolut will charge
              your saved card when the trial ends.
            </p>
          ) : (
            <p className="mt-2 text-sm text-[var(--text-secondary)] leading-relaxed">
              Your payment was confirmed. Subscription details are updated below.
            </p>
          )}
          {sub.orgStatus === "active" && !sub.grandfathered ? (
            <p className="mt-2 text-xs font-mono text-[var(--primary)]">
              Plan active · {sub.billingLabel || sub.plan}
            </p>
          ) : null}
        </div>
      ) : null}

      {access.message && !sub.grandfathered ? (
        <div className="mt-6 surface p-4 border-l-2 border-l-[var(--primary)] text-sm text-[var(--text-secondary)]">
          {access.message}
        </div>
      ) : null}

      {/* Current plan & upcoming */}
      <div className="mt-10 grid md:grid-cols-2 gap-4">
        <div className="surface p-6 border-l-2 border-l-[var(--primary)]">
          <div className="text-overline mb-2">Current plan</div>
          <div className="font-heading text-3xl">{sub.billingLabel || currentPlan || "—"}</div>
          <div className="mt-2 text-sm text-[var(--text-secondary)]">
            Status:{" "}
            <span className="font-mono text-[var(--text-primary)] capitalize">
              {onTrial ? "trial" : sub.orgStatus || "active"}
            </span>
            {onTrial && trialEndsAt ? (
              <span className="ml-2 text-[var(--text-tertiary)] text-xs font-mono">
                · ends {formatDate(trialEndsAt)}
              </span>
            ) : null}
            {sub.grandfathered ? (
              <span className="ml-2 text-[var(--primary)] text-xs uppercase tracking-widest">Live agreement</span>
            ) : null}
          </div>
          <div className="mt-4 text-xs font-mono text-[var(--text-tertiary)]">
            Team users: <span className="text-[var(--text-primary)]">{overview?.teamUsers ?? 0}</span>
          </div>
        </div>
        <div className="surface p-6">
          <div className="flex items-center gap-2 text-overline mb-2">
            <Calendar size={14} className="text-[var(--primary)]" />
            Upcoming billing
          </div>
          {sub.grandfathered ? (
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              Your workspace is on an <strong className="text-[var(--text-primary)]">enterprise agreement</strong> with no
              automated expiry. Contact <span className="font-mono">ops@aivoryems.com</span> for contract changes.
            </p>
          ) : onTrial && trialEndsAt ? (
            <>
              <div className="font-heading text-2xl text-[var(--primary)]">Trial ends</div>
              <div className="mt-2 font-mono text-sm">{formatDate(trialEndsAt)}</div>
              <p className="mt-3 text-xs text-[var(--text-tertiary)]">
                {sub.paymentMethodSaved
                  ? "Revolut will charge your saved payment method when the trial ends."
                  : "Add a payment method before this date to avoid interruption."}
              </p>
            </>
          ) : sub.currentPeriodEndsAt ? (
            <>
              <div className="font-heading text-2xl">Next renewal</div>
              <div className="mt-2 font-mono text-sm">{formatDate(sub.currentPeriodEndsAt)}</div>
            </>
          ) : (
            <p className="text-sm text-[var(--text-secondary)]">{sub.upcomingLabel || "Contact billing for your renewal date."}</p>
          )}
          <div className="mt-4 text-xs text-[var(--text-tertiary)]">
            Billing cycle: <span className="capitalize">{billingCycle}</span>
            {sub.revolutSubscriptionState ? (
              <span className="ml-2 font-mono">· Revolut {sub.revolutSubscriptionState}</span>
            ) : null}
          </div>
        </div>
      </div>

      {revolutEnabled && !sub.grandfathered ? (
        <div className="mt-6 surface p-6">
          <div className="text-overline mb-2">Subscription billing</div>
          <p className="text-xs text-[var(--text-tertiary)] max-w-xl">
            Annual billing saves 17% versus paying monthly. Changes apply to future Revolut charges.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              className={`btn-secondary text-sm ${billingCycle === "monthly" ? "ring-1 ring-[var(--primary)]" : ""}`}
              disabled={subscriptionBusy}
              onClick={() => saveBillingCycle("monthly")}
            >
              Monthly · {formatMoneyDisplay(monthlySgd, displayCurrency, usdToSgdRate)}/mo
            </button>
            <button
              type="button"
              className={`btn-secondary text-sm ${billingCycle === "annual" ? "ring-1 ring-[var(--primary)]" : ""}`}
              disabled={subscriptionBusy}
              onClick={() => saveBillingCycle("annual")}
            >
              Annual · {formatMoneyDisplay(annualSgd, displayCurrency, usdToSgdRate)}/yr (17% off)
            </button>
          </div>
          {!sub.paymentMethodSaved ? (
            <button
              type="button"
              className="btn-primary mt-4 text-sm"
              disabled={subscriptionBusy}
              onClick={() => startSubscriptionSetup(currentPlan)}
            >
              Set up Revolut billing
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Usage vs plan limits */}
      <div className="mt-10 surface p-6">
        <div className="text-overline mb-2">Usage</div>
        <h2 className="font-heading text-xl tracking-tight">Plan meters</h2>
        <p className="mt-2 text-xs text-[var(--text-tertiary)] max-w-xl">
          Live usage for your workspace against your current plan limits. Enterprise and grandfathered accounts may
          show higher caps or fair-use policies.
        </p>
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <UsageMeter
            label={usage.events?.label || "Events"}
            used={usage.events?.used ?? 0}
            limit={usage.events?.limit}
            percent={usage.events?.percent}
          />
          <UsageMeter
            label={usage.adminUsers?.label || "Admin users"}
            used={usage.adminUsers?.used ?? overview?.teamUsers ?? 0}
            limit={usage.adminUsers?.limit}
            percent={usage.adminUsers?.percent}
          />
          <UsageMeter
            label={usage.attendees?.label || "Registered attendees"}
            used={usage.attendees?.used ?? 0}
            limit={usage.attendees?.limit}
            percent={usage.attendees?.percent}
            hint={
              usage.maxAttendeesPerEvent
                ? `Per-event cap: ${usage.maxAttendeesPerEvent.toLocaleString()} attendees`
                : undefined
            }
          />
          <UsageMeter
            label={usage.emails?.label || "Emails sent"}
            used={usage.emails?.used ?? 0}
            limit={usage.emails?.limit}
            percent={usage.emails?.percent}
          />
          <UsageMeter
            label={usage.aiCredits?.label || "AI credits this month"}
            used={usage.aiCredits?.used ?? 0}
            limit={usage.aiCredits?.limit}
            percent={usage.aiCredits?.percent}
          />
        </div>
      </div>

      {/* Plan comparison */}
      <div className="mt-14">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-overline mb-2">Plans</div>
            <h2 className="font-heading text-2xl tracking-tight">Compare packages</h2>
          </div>
          <p className="text-xs text-[var(--text-tertiary)] max-w-md">
            Annual SGD pricing · self-serve checkout for Starter, Studio, and Scale
          </p>
        </div>
        <div className="mt-6 grid lg:grid-cols-4 md:grid-cols-2 gap-px bg-[var(--border-default)]">
          {plans.map((p) => {
            const isCurrent = p.id === currentPlan;
            const canUpgrade = planRank(p.id) > currentRank;
            return (
              <div
                key={p.id}
                className={`bg-[var(--bg-surface)] p-6 flex flex-col ${
                  p.highlight ? "ring-1 ring-inset ring-[var(--primary)]" : ""
                } ${isCurrent ? "bg-[var(--bg-elev)]" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-heading text-lg">{p.name}</div>
                  {isCurrent ? (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)]">Current</span>
                  ) : p.tag ? (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--text-tertiary)]">{p.tag}</span>
                  ) : null}
                </div>
                <div className="font-mono text-3xl mt-4">
                  {displayCurrency === "USD" && p.annualPriceSgd
                    ? formatMoneyDisplay(p.annualPriceSgd, "USD", usdToSgdRate)
                    : p.priceLabel}
                </div>
                <div className="text-xs text-[var(--text-tertiary)] mt-1">
                  {displayCurrency === "USD" ? "per year · converted from SGD" : p.priceSubtext}
                </div>
                <ul className="mt-6 space-y-2 flex-1">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex gap-2 text-xs text-[var(--text-secondary)] leading-relaxed">
                      <Check size={14} className="shrink-0 text-[var(--primary)] mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  {isCurrent ? (
                    <button type="button" className="btn-secondary w-full justify-center text-sm opacity-60" disabled>
                      Current plan
                    </button>
                  ) : canUpgrade && p.selfServeCheckout !== false ? (
                    <button
                      type="button"
                      className={p.highlight ? "btn-primary w-full justify-center text-sm" : "btn-secondary w-full justify-center text-sm"}
                      disabled={subscriptionBusy}
                      onClick={() => subscribeToPlan(p.id)}
                    >
                      Subscribe annually <ArrowUpRight size={14} />
                    </button>
                  ) : p.id === "enterprise" ? (
                    <Link
                      to="/contact?intent=sales"
                      className="btn-secondary w-full justify-center text-sm inline-flex items-center gap-1"
                    >
                      Contact sales <ArrowUpRight size={14} />
                    </Link>
                  ) : (
                    <button type="button" className="btn-ghost w-full justify-center text-sm opacity-50" disabled>
                      —
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <BillingAddOnCart
        catalog={overview?.addOnCatalog || []}
        revolutEnabled={revolutEnabled}
        onCheckoutComplete={load}
      />

      {/* Billing email & controls */}
      <div className="mt-10 surface p-6 max-w-xl">
        <div className="flex items-center gap-2 text-overline mb-4">
          <Mail size={14} />
          Billing contact
        </div>
        <input
          className="input-dark w-full"
          type="email"
          value={settings.billingEmail || ""}
          onChange={(e) => setSettings((s) => ({ ...s, billingEmail: e.target.value }))}
        />
        <label className="flex items-center gap-2 text-sm mt-4">
          <input
            type="checkbox"
            className="accent-[var(--primary)]"
            checked={Boolean(settings.autoRecharge)}
            onChange={(e) => setSettings((s) => ({ ...s, autoRecharge: e.target.checked }))}
          />
          Request auto-recharge when credits run low (requires approval)
        </label>
        <div className="mt-3">
          <label className="text-xs text-[var(--text-tertiary)]">Monthly spend alert ({displayCurrency})</label>
          <input
            type="number"
            min="0"
            className="input-dark w-full mt-1 max-w-xs"
            value={Number(settings.monthlyLimitUsd || 0)}
            onChange={(e) => setSettings((s) => ({ ...s, monthlyLimitUsd: Number(e.target.value || 0) }))}
          />
        </div>
        <button type="button" className="btn-primary mt-4 text-sm" onClick={saveSettings} disabled={savingSettings}>
          {savingSettings ? "Saving…" : "Save contact settings"}
        </button>
      </div>

      {/* Invoice history */}
      <div className="mt-14">
        <div className="flex items-center gap-2 text-overline mb-2">
          <CreditCard size={14} />
          Billing history
        </div>
        <h2 className="font-heading text-xl">Past invoices</h2>
        <div className="mt-6 surface overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
              <tr>
                <th className="p-4 text-left">Date</th>
                <th className="p-4 text-left">Description</th>
                <th className="p-4 text-left">Plan</th>
                <th className="p-4 text-right">Amount</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-[var(--text-tertiary)]">
                    No invoices yet. They will appear here when issued by billing or at renewal.
                  </td>
                </tr>
              ) : (
                invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-[var(--border-default)]">
                    <td className="p-4 font-mono text-xs">{inv.invoiceNumber || formatDate(inv.issuedAt)}</td>
                    <td className="p-4">{inv.description}</td>
                    <td className="p-4 capitalize">{inv.plan || "—"}</td>
                    <td className="p-4 text-right font-mono">{invoiceAmount(inv, displayCurrency, usdToSgdRate)}</td>
                    <td className="p-4">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)]">{inv.status}</span>
                    </td>
                    <td className="p-4 text-right space-x-2">
                      <button type="button" className="btn-ghost text-xs" onClick={() => downloadInvoice(inv.id)}>
                        Download
                      </button>
                      {["issued", "pending", "overdue"].includes(inv.status) ? (
                        <button type="button" className="btn-secondary text-xs" disabled={subscriptionBusy} onClick={() => payInvoice(inv.id)}>
                          Pay now
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
